/**
 * AI-Powered Resume Parser Service
 * Uses backend API endpoints for secure OpenAI integration
 */

import { parseResume, ParsedResume } from './resumeParserService';
import { authenticatedFetch } from '../lib/apiClient';

export interface AIEnhancedResume extends ParsedResume {
  // Enhanced fields from AI
  yearsOfExperience?: number;
  careerLevel?: 'entry' | 'mid' | 'senior' | 'lead' | 'executive';
  techStack?: string[];
  industries?: string[];
  certifications?: string[];
  languages?: Array<{ language: string; proficiency: string }>;
  softSkills?: string[];
  achievements?: string[];

  // AI Analysis
  aiSummary?: string;
  strengths?: string[];
  gaps?: string[];
}

export interface AIMatchingResult {
  overallScore: number;
  confidence: 'high' | 'medium' | 'low';

  // Detailed scoring
  skillsMatch: {
    score: number;
    matchedSkills: string[];
    missingSkills: string[];
    additionalSkills: string[];
  };

  experienceMatch: {
    score: number;
    reasoning: string;
  };

  educationMatch: {
    score: number;
    reasoning: string;
  };

  cultureFit?: {
    score: number;
    reasoning: string;
  };

  // AI Insights
  summary: string;
  strengths: string[];
  concerns: string[];
  recommendations: string[];
  interviewQuestions?: string[];
}

/**
 * Parse resume using AI for enhanced extraction
 */
export async function parseResumeWithAI(file: File): Promise<AIEnhancedResume> {
  try {
    // First, use basic parser to extract text
    const basicParsed = await parseResume(file);

    // Validate that text was actually extracted
    const extractedText = basicParsed.extractedText?.trim() || '';

    console.log(`[AI Service] Extracted text length: ${extractedText.length} chars from ${file.name}`);

    // Check if we got meaningful text (at least 50 characters)
    if (extractedText.length < 50) {
      console.warn(`[AI Service] Very little text extracted from ${file.name}: "${extractedText.substring(0, 100)}"`);
      throw new Error(`Could not extract text from resume. The file may be a scanned image, password-protected, or in an unsupported format. Please try a different PDF or use a DOCX file.`);
    }

    // Check if the text looks like garbage (too many special chars, no spaces, etc.)
    const alphanumericRatio = (extractedText.match(/[a-zA-Z0-9\s]/g) || []).length / extractedText.length;
    if (alphanumericRatio < 0.5) {
      console.warn(`[AI Service] Text appears corrupted (${(alphanumericRatio * 100).toFixed(1)}% alphanumeric): "${extractedText.substring(0, 100)}"`);
      throw new Error(`The resume text appears corrupted or unreadable. This may be a scanned document or use unsupported fonts. Please try a different file format.`);
    }

    // Then enhance with AI
    const aiEnhanced = await enhanceResumeWithAI(extractedText);

    // Validate AI response - check for placeholder values
    if (aiEnhanced.name === 'John Doe' || aiEnhanced.name === 'N/A' || !aiEnhanced.name) {
      console.warn(`[AI Service] AI returned placeholder name for ${file.name}, extracted text preview: "${extractedText.substring(0, 200)}"`);
    }

    return {
      ...basicParsed,
      ...aiEnhanced
    };
  } catch (error: any) {
    console.error('[AI Service] Error parsing resume:', error.message);
    throw error;
  }
}

/**
 * Enhance resume data using AI via backend API
 */
async function enhanceResumeWithAI(resumeText: string): Promise<Partial<AIEnhancedResume>> {
  try {
    const response = await authenticatedFetch('/api/parse-resume', {
      method: 'POST',
      body: JSON.stringify({ resumeText })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || 'Failed to parse resume');
    }

    const result = await response.json();

    if (!result.success || !result.data) {
      throw new Error('Invalid response from backend');
    }

    return result.data;
  } catch (error: any) {
    console.error('[AI Service] Enhancement error:', error.message);
    throw error;
  }
}

/**
 * Match resume against job description using AI via backend API
 */
export async function matchResumeWithJD(
  resume: AIEnhancedResume,
  jobDescription: {
    title: string;
    description: string;
    requirements: string[];
    benefits?: string[];
    responsibilities?: string[];
    location?: string;
    workType?: string;
  }
): Promise<AIMatchingResult> {
  try {
    const response = await authenticatedFetch('/api/match-resume', {
      method: 'POST',
      body: JSON.stringify({ resume, jobDescription })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || 'Failed to match resume');
    }

    const result = await response.json();

    if (!result.success || !result.data) {
      throw new Error('Invalid response from backend');
    }

    return result.data;
  } catch (error: any) {
    console.error('[AI Service] Matching error:', error.message);
    throw error;
  }
}

/**
 * Batch analyze multiple resumes for ranking
 */
export async function batchAnalyzeResumes(
  resumes: Array<{ id: string; resume: AIEnhancedResume }>,
  jobDescription: any
): Promise<Array<{ id: string; result: AIMatchingResult }>> {
  const results = [];

  // Process in batches to avoid rate limits
  for (const { id, resume } of resumes) {
    try {
      const result = await matchResumeWithJD(resume, jobDescription);
      results.push({ id, result });

      // Add small delay to respect rate limits
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error(`Error analyzing resume ${id}:`, error);
      results.push({
        id,
        result: {
          overallScore: 0,
          confidence: 'low',
          summary: 'Analysis failed',
          strengths: [],
          concerns: ['Unable to analyze resume'],
          recommendations: [],
          skillsMatch: {
            score: 0,
            matchedSkills: [],
            missingSkills: [],
            additionalSkills: []
          },
          experienceMatch: {
            score: 0,
            reasoning: 'Analysis unavailable'
          },
          educationMatch: {
            score: 0,
            reasoning: 'Analysis unavailable'
          }
        } as AIMatchingResult
      });
    }
  }

  // Sort by overall score
  return results.sort((a, b) => b.result.overallScore - a.result.overallScore);
}

/**
 * Generate interview questions based on resume and JD via backend API
 */
export async function generateInterviewQuestions(
  resume: AIEnhancedResume,
  jobDescription: any,
  focusAreas?: string[]
): Promise<{
  technical: string[];
  behavioral: string[];
  situational: string[];
  cultural: string[];
}> {
  try {
    const response = await authenticatedFetch('/api/generate-questions', {
      method: 'POST',
      body: JSON.stringify({ resume, jobDescription, focusAreas })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || 'Failed to generate questions');
    }

    const result = await response.json();

    if (!result.success || !result.data) {
      throw new Error('Invalid response from backend');
    }

    return result.data;
  } catch (error: any) {
    console.error('[AI Service] Question generation error:', error.message);
    throw error;
  }
}

/**
 * Check if AI features are available
 * Returns true by default - AI features will fail gracefully if not configured
 */
export function isAIEnabled(): boolean {
  // AI is enabled by default - individual API calls will fail if OpenAI key not configured
  return true;
}

/**
 * Async version of isAIEnabled
 * Returns true by default - AI features will fail gracefully if not configured
 */
export async function checkAIEnabled(): Promise<boolean> {
  // AI is enabled by default - individual API calls will fail if OpenAI key not configured
  return true;
}

/**
 * Get pricing estimate for AI operations
 */
export function getAIPricingEstimate(operationType: 'parse' | 'match' | 'batch' | 'questions'): string {
  const estimates = {
    parse: '$0.02 - $0.05 per resume',
    match: '$0.03 - $0.08 per analysis',
    batch: '$0.20 - $0.50 per 10 resumes',
    questions: '$0.01 - $0.03 per set'
  };

  return estimates[operationType];
}
